import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { API } from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { CheckCircle2, Clock, Copy, Download, LogIn, Loader2, XCircle } from "lucide-react";
import { toast } from "sonner";

const MAX_POLLS = 15;
const POLL_INTERVAL_MS = 2000;
// A deferred method can finish the payment page and confirm the money seconds
// — or hours — later. We keep asking slowly for about a minute so a quick one
// flips to "paid" while the buyer is still here, and then stop: nobody should
// be asked to sit on a page waiting for a bank.
const SLOW_POLL_INTERVAL_MS = 5000;
const MAX_SLOW_POLLS = 12;
// MONEI confirms a moment AFTER sending the buyer back, so the first seconds
// here mean nothing. No bad news is shown before this window is over.
const GRACE_MS = 5000;

export default function PaymentSuccessPage() {
  const [params] = useSearchParams();
  // MONEI sends the buyer back with ?id=<pago>. `tx` is our own fallback: unlike
  // Stripe, MONEI has no "put the payment id here" placeholder for the return
  // URL, so we also thread our transaction id through it.
  const sessionId = params.get("id") || params.get("tx");
  // MONEI also appends ?status=…, but it is deliberately ignored: their own
  // docs say the redirect proves nothing. Only our server decides.
  // The access_token is emitted by /checkout and threaded into the return URL
  // so the poll+download endpoints (SEC-004 mitigation) can prove ownership of
  // this purchase even without a user account.
  const accessToken = params.get("access_token");
  const [state, setState] = useState({ phase: "checking", data: null });
  // Two counters on purpose: the ref is what the polling loop reads (the
  // effect closes over its state ONCE, so a state counter stays frozen at 0
  // there and the budget never runs out — the page used to poll forever), and
  // the state one only feeds the "intento N/M" line.
  const attemptsRef = useRef(0);
  const [attempts, setAttempts] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (!sessionId) {
      setState({ phase: "error", data: { message: "Falta la referencia del pago" } });
      return;
    }
    let cancelled = false;
    const arrivedAt = Date.now();
    // Time left before we are allowed to give bad news.
    const graceLeft = () => Math.max(0, GRACE_MS - (Date.now() - arrivedAt));
    let slow = false;

    async function poll() {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`, {
          params: accessToken ? { access_token: accessToken } : undefined,
        });
        if (cancelled) return;
        if (data.payment_status === "paid" || data.phase === "paid") {
          setState({ phase: "paid", data });
          return;
        }
        const refused =
          data.phase === "failed"
            ? "failed"
            : data.status === "expired" || data.phase === "expired"
            ? "expired"
            : null;
        if (refused) {
          // Inside the grace window we simply ask again instead of telling the
          // buyer their payment failed while the gateway is still deciding.
          const wait = graceLeft();
          if (wait > 0) {
            setTimeout(poll, wait);
            return;
          }
          setState({ phase: refused, data });
          return;
        }
        // Payment page finished, funds still settling: show the reassurance
        // card right away and keep checking quietly in the background.
        if (data.phase === "processing") slow = true;
        if (slow) setState({ phase: "processing", data });
        attemptsRef.current += 1;
        setAttempts(attemptsRef.current);
        if (attemptsRef.current >= (slow ? MAX_SLOW_POLLS : MAX_POLLS)) {
          if (!slow) {
            // Still unconfirmed after the fast window. That is not a failure:
            // move to the calm card and keep asking slowly.
            slow = true;
            attemptsRef.current = 0;
            setAttempts(0);
            setState({ phase: "processing", data });
            setTimeout(poll, SLOW_POLL_INTERVAL_MS);
            return;
          }
          setState({ phase: "timeout", data });
          return;
        }
        setTimeout(poll, slow ? SLOW_POLL_INTERVAL_MS : POLL_INTERVAL_MS);
      } catch (err) {
        if (cancelled) return;
        // An error here is almost always temporary (a hiccup, or the gateway
        // taking its time), and the callback finishes the purchase anyway. So
        // we keep asking on the same budget instead of stopping at the first
        // one, and only give up when the budget is spent.
        attemptsRef.current += 1;
        setAttempts(attemptsRef.current);
        if (attemptsRef.current < (slow ? MAX_SLOW_POLLS : MAX_POLLS)) {
          setTimeout(poll, Math.max(graceLeft(), slow ? SLOW_POLL_INTERVAL_MS : POLL_INTERVAL_MS));
          return;
        }
        setState({
          phase: slow ? "timeout" : "error",
          data: { message: err?.response?.data?.detail || "Error de red" },
        });
      }
    }
    attemptsRef.current = 0;
    poll();
    return () => {
      cancelled = true;
    };
    // Keyed on the session alone on purpose: the loop reschedules itself and
    // reads its counter from the ref, so re-running the effect on every
    // attempt would start a second loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, accessToken]);

  const copyPassword = (pw) => {
    navigator.clipboard.writeText(pw);
    toast.success("Contraseña copiada");
  };

  return (
    <div className="min-h-screen bg-[#F4F6F4] flex items-center justify-center px-4" data-testid="payment-success-page">
      <Card className="max-w-lg w-full p-8 border border-gray-200 shadow-none rounded-lg">
        {state.phase === "checking" && (
          <div className="text-center space-y-3" data-testid="payment-checking">
            <Loader2 className="w-10 h-10 mx-auto text-brand animate-spin" />
            <h1 className="font-display text-2xl text-brand-ink">Confirmando pago…</h1>
            <p className="text-sm text-gray-500">
              Estamos comprobando el estado con la pasarela de pago. No cierres esta página.
            </p>
            <p className="text-[10px] text-gray-400 font-mono-data">intento {attempts + 1}/{MAX_POLLS}</p>
          </div>
        )}

        {state.phase === "paid" && (
          <div className="space-y-5" data-testid="payment-paid">
            <div className="text-center">
              <CheckCircle2 className="w-12 h-12 mx-auto text-brand" />
              <h1 className="font-display text-3xl text-brand-ink mt-3">¡Compra completada!</h1>
              <p className="text-sm text-gray-500 mt-1">
                Importe: <span className="font-mono-data">{state.data.amount_eur?.toFixed(2)} €</span>
                {state.data.invoice_number ? (
                  <>
                    {" "}· Factura{" "}
                    <span className="font-mono-data">{state.data.invoice_number}</span>
                  </>
                ) : null}
              </p>
            </div>

            {state.data.generated_password ? (
              <div className="border border-brand/30 bg-brand/5 rounded-lg p-4" data-testid="new-account-block">
                <p className="text-xs uppercase tracking-wider text-brand font-mono-data mb-2">
                  Cuenta creada
                </p>
                <p className="text-sm text-brand-ink">
                  Hemos creado una cuenta para <strong>{state.data.email}</strong>. Guarda esta
                  contraseña temporal — <strong>no volveremos a mostrarla</strong>:
                </p>
                <div className="flex items-center gap-2 mt-3">
                  <code
                    className="flex-1 bg-white border border-gray-200 rounded-md px-3 py-2 font-mono-data text-brand-ink text-sm"
                    data-testid="new-account-password"
                  >
                    {state.data.generated_password}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyPassword(state.data.generated_password)}
                    data-testid="copy-password-btn"
                  >
                    <Copy className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </div>
            ) : (
              <div className="border border-gray-200 rounded-lg p-4 bg-white" data-testid="existing-account-block">
                <p className="text-sm text-gray-700">
                  Los cursos ya se han añadido a tu cuenta <strong>{state.data.email}</strong>.
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              {state.data.invoice_number && (
                <a
                  href={`${API}/payments/invoice/${sessionId}${accessToken ? `?access_token=${encodeURIComponent(accessToken)}` : ""}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex-1"
                  data-testid="download-invoice-btn"
                >
                  <Button variant="outline" className="w-full">
                    <Download className="w-4 h-4 mr-2" />
                    Descargar justificante de pago
                  </Button>
                </a>
              )}
              <Button
                onClick={() => navigate("/login")}
                className="flex-1 bg-brand hover:bg-brand-hover text-white"
                data-testid="go-to-login-btn"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Iniciar sesión
              </Button>
            </div>
            <p className="text-[11px] text-gray-500 text-center">
              También te enviaremos un email de confirmación con estos datos.
            </p>
          </div>
        )}

        {/* Checkout finished, the money is still being confirmed. Deliberately
            NOT styled as an error: the purchase will very probably go through,
            and the worst outcome here is a buyer paying twice out of doubt. */}
        {state.phase === "processing" && (
          <div className="text-center space-y-3" data-testid="payment-processing">
            <Clock className="w-10 h-10 mx-auto text-brand" />
            <h1 className="font-display text-2xl text-brand-ink">Estamos verificando tu pago</h1>
            <p className="text-sm text-gray-600">
              Tu pedido está en verificación con la entidad de pago. Suele tardar unos segundos,
              pero algunos métodos confirman en diferido y pueden tardar más.
            </p>
            <p className="text-sm font-medium text-brand-ink bg-brand-soft/60 border border-[#bcdfd9] rounded-md p-3">
              No vuelvas a pagar ni hace falta que dejes esta página abierta. En cuanto se
              confirme te crearemos el acceso y te enviaremos un email con tu justificante.
            </p>
            <Link to="/">
              <Button variant="outline">Volver al catálogo</Button>
            </Link>
          </div>
        )}

        {state.phase === "failed" && (
          <div className="text-center space-y-3" data-testid="payment-failed">
            <XCircle className="w-10 h-10 mx-auto text-red-600" />
            <h1 className="font-display text-2xl text-brand-ink">El pago no se ha completado</h1>
            <p className="text-sm text-gray-600">
              La entidad de pago no ha autorizado la operación y <strong>no se te ha cobrado
              nada</strong>. Puedes volver a intentarlo desde el catálogo, con este método o con otro.
            </p>
            <p className="text-xs text-gray-500">
              Si crees que es un error, escríbenos a{" "}
              <a href="mailto:hablamos@sima.madrid" className="text-brand hover:underline">
                hablamos@sima.madrid
              </a>.
            </p>
            <Link to="/">
              <Button variant="outline">Volver al catálogo</Button>
            </Link>
          </div>
        )}

        {(state.phase === "timeout" || state.phase === "expired" || state.phase === "error") && (
          <div className="text-center space-y-3" data-testid="payment-error">
            <XCircle className="w-10 h-10 mx-auto text-amber-600" />
            <h1 className="font-display text-2xl text-brand-ink">
              {state.phase === "expired"
                ? "Sesión expirada"
                : state.phase === "timeout"
                ? "Estamos tardando más de lo normal"
                : "No pudimos confirmar el pago"}
            </h1>
            <p className="text-sm text-gray-500">
              Todavía no tenemos la confirmación de la entidad de pago. <strong>No repitas el
              pago</strong>: si el cobro se llegó a hacer, recibirás por email tu acceso y tu
              justificante en unos minutos.
            </p>
            <Link to="/">
              <Button variant="outline">Volver al catálogo</Button>
            </Link>
          </div>
        )}
      </Card>
    </div>
  );
}
